import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { scaleLinear } from 'd3-scale';
import { cn } from '../utils/cn';

const KeywordCloud = ({ tokens, stats, onTokenClick, selectedToken }) => {
  const [viewMode, setViewMode] = useState('volume');

  const sizeScale = useMemo(() => {
    const maxVal = Math.max(...tokens.map(t => viewMode === 'opportunity' ? t.opportunity : t.totalVolume));
    const minVal = Math.min(...tokens.map(t => viewMode === 'opportunity' ? t.opportunity : t.totalVolume));
    return scaleLinear()
      .domain([minVal, maxVal])
      .range([0.8, 3.2]);
  }, [tokens, viewMode]);

  const colorScale = useMemo(() => {
    const maxFreq = Math.max(...tokens.map(t => t.frequency));
    return scaleLinear()
      .domain([1, maxFreq])
      .range(['#fbbf24', '#d97706']); // yellow-400 to yellow-600
  }, [tokens]);

  const sortedTokens = useMemo(() => {
    return [...tokens]
      .sort((a, b) => {
        if (viewMode === 'opportunity') return b.opportunity - a.opportunity;
        return b.totalVolume - a.totalVolume;
      })
      .slice(0, 80);
  }, [tokens, viewMode]);

  return (
    <div className="w-full h-full flex flex-col space-y-4">
      <div className="flex items-center justify-between px-2">
        <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Semantic Cloud</h3>
        <div className="flex bg-slate-100 dark:bg-slate-800 rounded-lg p-1">
          {['volume', 'opportunity', 'frequency'].map(mode => (
            <button
              key={mode}
              onClick={() => setViewMode(mode)}
              className={cn(
                "px-3 py-1 text-[10px] font-black uppercase tracking-wider rounded-md transition-all",
                viewMode === mode 
                  ? "bg-white dark:bg-slate-700 text-yellow-600 dark:text-yellow-400 shadow-sm" 
                  : "text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
              )}
            >
              {mode}
            </button>
          ))}
        </div>
      </div>
      <div className="relative flex-1 bg-white dark:bg-slate-900 rounded-2xl sm:rounded-3xl border border-slate-200 dark:border-slate-800 p-6 sm:p-10 overflow-hidden">
        <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-4 h-full overflow-y-auto custom-scrollbar">
          <AnimatePresence>
            {sortedTokens.map((token) => {
              const size = sizeScale(viewMode === 'opportunity' ? token.opportunity : token.totalVolume);
              const color = colorScale(token.frequency);
              const isSelected = selectedToken?.token === token.token;

              return (
                <motion.button
                  key={token.token}
                  layout
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  whileHover={{ scale: 1.1, zIndex: 10 }}
                  onClick={() => onTokenClick(token)}
                  className={cn(
                    "relative font-black leading-none cursor-pointer transition-all duration-200 select-none group",
                    isSelected ? "text-yellow-600 dark:text-yellow-400 scale-110 drop-shadow-md" : ""
                  )}
                  style={{ 
                    fontSize: `${size}rem`,
                    color: isSelected ? undefined : color,
                    opacity: isSelected ? 1 : 0.8
                  }}
                >
                  {token.token}
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-3 w-max hidden group-hover:block z-50 pointer-events-none">
                    <div className="bg-slate-900 dark:bg-slate-800 text-white text-[10px] rounded-xl py-2 px-3 shadow-2xl flex flex-col items-center gap-1 border border-slate-700/50">
                      <span className="font-black text-sm capitalize text-yellow-400">{token.token}</span>
                      <div className="flex items-center gap-3 font-bold uppercase tracking-widest text-slate-400">
                        <span>Vol: {token.totalVolume.toLocaleString()}</span>
                        <span>Freq: {token.frequency}</span>
                      </div>
                    </div>
                    <div className="w-2.5 h-2.5 bg-slate-900 dark:bg-slate-800 rotate-45 absolute -bottom-1 left-1/2 -translate-x-1/2 border-r border-b border-slate-700/50"></div>
                  </div>
                </motion.button>
              );
            })}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default KeywordCloud;